import { useRef, useEffect, useState } from 'react';

const localServices = [
  {
    icon: 'ri-layout-grid-line',
    title: 'Carpet Installation in Freehold',
    desc: 'From plush residential carpet to commercial carpet tiles — installed with precision for homes and businesses across Freehold, NJ.',
  },
  {
    icon: 'ri-home-3-line',
    title: 'Hardwood Flooring in Freehold',
    desc: 'Solid and engineered hardwood flooring options installed by specialists serving Freehold and Monmouth County homeowners.',
  },
  {
    icon: 'ri-droplet-line',
    title: 'Vinyl & Laminate Flooring',
    desc: 'Waterproof vinyl and durable laminate flooring for kitchens, bathrooms, and high-traffic areas throughout Freehold, NJ.',
  },
  {
    icon: 'ri-anchor-line',
    title: 'Marine & Specialty Carpet',
    desc: 'UV-resistant marine carpet and specialty turf installed for unique residential, commercial, and marine applications.',
  },
];

export default function LocalServices() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} id="services" className="py-24 lg:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div
          className="max-w-2xl mb-16"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="section-tag mb-4 block">Local Flooring Services</span>
          <h2 className="section-heading mb-6">
            Flooring Installation<br />
            <em className="text-gold not-italic">in Freehold, NJ</em>
          </h2>
          <p className="text-warm-gray text-base leading-relaxed">
            Value Carpet Inc serves Freehold and the surrounding Monmouth County area with professional flooring installation — for every room, every budget, and every style.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {localServices.map((svc, i) => (
            <div
              key={svc.title}
              className="bg-white rounded-lg p-8 border border-cream-dark hover:border-gold/30 transition-colors duration-300"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${0.1 + i * 0.1}s, transform 0.6s ease ${0.1 + i * 0.1}s`,
              }}
            >
              <div className="flex items-start gap-5">
                <span className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-charcoal">
                  <i className={`${svc.icon} text-gold text-lg`}></i>
                </span>
                <div>
                  <h3 className="font-serif text-xl text-charcoal mb-3">{svc.title}</h3>
                  <p className="text-warm-gray text-sm leading-relaxed">{svc.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="bg-charcoal rounded-lg px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.7s ease 0.4s',
          }}
        >
          <div>
            <p className="font-serif text-white text-lg mb-1">Ready to transform your Freehold home or business?</p>
            <p className="text-white/50 text-sm">Same-day quotes available. We respond within minutes.</p>
          </div>
          <button onClick={scrollToContact} className="btn-primary whitespace-nowrap flex-shrink-0">
            Get a Free Quote
          </button>
        </div>
      </div>
    </section>
  );
}
